import { parseUnitToNum } from "../../../../utils/cssunitparser";

interface Item {
    ref: string;
    el: HTMLElement;
    inView: boolean;
}

const MENU_HEIGHT = parseUnitToNum(getComputedStyle(document.documentElement).getPropertyValue('--header-height'));

const SCROLL_AFFORDANCE = {
    TOP: 'top',
    BOTH: 'both',
    BOTTOM: 'bottom',
    LEFT: 'left',
    RIGHT: 'right',
    NONE: 'none'
  };

const ATTRS = [
    'accent-color',
    'download-button',
    'download-label',
    'reading-time',
    'selector'
];

export class PageIndexElement extends HTMLElement {
    private _active: number;
    private _click: (e) => void;
    private _scroll: (e) => void;
    private _itemObserver: IntersectionObserver;
    private _navObserver: IntersectionObserver;
    private readonly _items: Item[];
    private readonly mediaQuery: MediaQueryList = window.matchMedia('(min-width: 768px)');
    private mediaQueryHandler: (e) => void;

    constructor() {
        super();
        this._active = 0;
        this._items = [];
    }

    static get observedAttributes() {
        return ATTRS;
    }

    public connectedCallback(): void {

        const selector = this.getAttribute('selector') || 'h1,h2,h3,h4,h5,h6';
        // next micro-task
        Promise.resolve().then(() => {
            const nav = this.querySelector('nav');
            const navHeight = nav.offsetHeight;
            // this.style.minHeight = navHeight + 'px'; // keep dimensions
            this.initState(nav);
            this._click = e => {
                const target = e.target as HTMLElement;
                if (target.tagName === 'A') {
                    const href = target.getAttribute('href');
                    try {
                        const el = document.querySelector(href);
                        if (el) {
                            this._active = this._items.findIndex(el => el.ref === href);
                            if (el.scrollIntoView) {
                                e.preventDefault();
                                el.scrollIntoView({
                                    behavior: 'smooth'
                                });
                            }
                        }
                    } catch {
                        console.error(`Invalid href ${href}`);
                    }
                } else if (target.closest('.carousel-control-prev')) {
                    index.scrollTo({ left: index.scrollLeft - 200, behavior: "smooth" });
                } else if (target.closest('.carousel-control-next')) {
                    index.scrollTo({ left: index.scrollLeft + 200, behavior: "smooth" });
                }
            };

            this._scroll = e =>  {
                const { scrollWidth, clientWidth, scrollLeft } = e.target;
                let affordance: string;
                if (scrollWidth <= clientWidth) {
                    affordance = SCROLL_AFFORDANCE.NONE;
                } else if (scrollLeft > 0 && scrollLeft < scrollWidth - clientWidth) {
                    affordance = SCROLL_AFFORDANCE.BOTH;
                } else if (scrollLeft <= 0) {
                    affordance = SCROLL_AFFORDANCE.RIGHT;
                } else {
                    affordance = SCROLL_AFFORDANCE.LEFT;
                }
                const nav = this.querySelector('nav');
                nav.setAttribute('data-scroll-affordance', affordance);
            };

            nav.addEventListener('click', this._click);

            const index = this.querySelector('div#pageIndexItems');
            index.addEventListener('scroll', this._scroll);
            this._scroll({ target: index });
            
            this.mediaQueryHandler = e => {
                if (e.matches) {
                    this._scroll({ target: index });
                }
            }
            this.mediaQuery.addEventListener("change", this.mediaQueryHandler);

            this._itemObserver = new IntersectionObserver(entries => {
                // update items status
                for (const e of entries) {
                    const ref = `#${e.target.id}`;
                    const item = this._items.find(it => it.ref === ref);
                    if (item) {
                        item.inView = e.isIntersecting;
                    }
                }

                // find first in view
                const firstInView = this._items.findIndex(it => it.inView);

                if (firstInView >= 0) {
                    this._active = firstInView;
                }

                // toggle active class
                for (let i = 0; i < this._items.length; i++) {
                    this._items[i].el.classList.toggle('active', i === this._active);
                }
            }, {
                rootMargin: `-${MENU_HEIGHT + navHeight}px 0px 0px 0px`,
                threshold: 0
            });

            if (selector) {
                for (const h of Array.from(document.querySelectorAll(selector))) {
                    this._itemObserver.observe(h);
                }
            }

            this._navObserver = new IntersectionObserver(entries => {
                for (const e of entries) {
                    // ['fixed'].map(c => nav.classList.toggle(c, !e.isIntersecting));
                    nav.classList.toggle('fixed-nav', !e.isIntersecting && e.target.getBoundingClientRect().top < 0);
                }
            });
            this._navObserver.observe(this);
        });
    }

    private initState(nav: HTMLElement) {
        const items: Item[] = Array.from(nav.querySelectorAll('li'), li => ({
            ref: li.querySelector('a').getAttribute('href'),
            el: li,
            inView: false
        }));
        
        this._items.splice(0, this._items.length, ...items); // keep ref
    }

    public disconnectedCallback(): void {
        const nav = this.querySelector('nav');
        nav.removeEventListener('click', this._click);
        const index = this.querySelector('div#pageIndexItems');
        index.removeEventListener('scroll', this._scroll);
        this._itemObserver.disconnect();
        this._navObserver.disconnect();
        this.mediaQuery.removeEventListener("change", this.mediaQueryHandler);
    }

    public attributeChangedCallback(name: string, oldVal: string, newVal: string): void {
        if (!ATTRS.includes(name)) {
            return;
        }

        if (name === 'selector') {
            Promise.resolve().then(() => { // microtask
                if (oldVal) {
                    for (const h of document.querySelectorAll(oldVal)) {
                        if (this._itemObserver) {
                            this._itemObserver.unobserve(h);
                        }
                    }
                }
                if (newVal) {
                    for (const h of document.querySelectorAll(newVal)) {
                        if (this._itemObserver) {
                            this._itemObserver.observe(h);
                        }
                    }
                }
                const nav = this.querySelector('nav');
                this.initState(nav);
            });
        }
    }
}
